import { useContext, useEffect, useState } from 'react';
import convertRequestStateToSearchParams from '../../services/convertRequestStateToSearchParams';
import { isValidEmail } from '../../services/emailUtils';
import { requestResetPassword } from '../../veevaIdApi';
import ResponseStatus from '../../constants/ResponseStatus';
import PasswordResetConfirmation from './PasswordResetConfirmation';
import PasswordResetForm from './PasswordResetForm';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import PageContainer from '../../components/PageContainer';
import { CaptchaVerificationPage } from '../../components/CaptchaVerification';
import useRequestState from '../../services/useRequestState';
import useMessageService from '../../services/i18n/useMessageService';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import {
    getErrorMessage,
    getFirstError,
    showErrorNotificationFromResponse,
} from '../../services/errorUtils';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import { useFriendlyCaptcha } from '../../services/useFriendlyCaptcha';

function ForgotPasswordPage() {
    const [
        [
            confirmationSentTitle = 'Confirmation Sent',
            forgotPasswordTitle = 'Forgot Password?',
            invalidEmailError = 'Invalid email format entered',
        ],
    ] = useMessageService([
        'forgot_password.confirmation_sent_title',
        'forgot_password.label_with_question_mark',
        'general.invalid_email',
    ]);
    const { setLoading } = useContext(LoadingScreenContext);
    const [email, setEmail] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { showNotification } = useNetworkErrorNotification();
    const requestState = useRequestState();
    const requestStateParams = convertRequestStateToSearchParams(requestState);
    const { verifyCaptcha, showCaptcha, captchaWidget, cancelCaptcha, captchaError } =
        useFriendlyCaptcha();

    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);

    useEffect(() => {
        setErrorMessage('');
    }, [messageLanguage]);

    const handleResetPassword = async () => {
        if (!isValidEmail(email)) {
            setErrorMessage(invalidEmailError);
            return;
        }

        setErrorMessage('');
        const frcToken = await verifyCaptcha();
        if (!frcToken) {
            return;
        }
        setLoading(true);
        const response = await requestResetPassword(email, frcToken, requestState);
        setLoading(false);
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                setShowConfirmation(true);
                break;
            case ResponseStatus.FAILURE: {
                const responseError = getFirstError(response);
                switch (responseError.type) {
                    case ResponseErrorTypes.VEEVAID_INVALID_RECAPTCHA: {
                        const message = await getErrorMessage(responseError, messageLanguage);
                        setErrorMessage(message);
                        break;
                    }
                    default: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNotification();
                break;
            default:
            // No-op
        }
    };

    const handleInputChange = (value) => {
        setEmail(value);
        setErrorMessage(''); // clear error as user types new email
    };

    const containerTitle = showConfirmation ? confirmationSentTitle : forgotPasswordTitle;

    return (
        <>
            <div hidden={showCaptcha}>
                <PageContainer title={containerTitle}>
                    {showConfirmation ? (
                        <PasswordResetConfirmation email={email} />
                    ) : (
                        <PasswordResetForm
                            onResetPassword={handleResetPassword}
                            onInputChange={handleInputChange}
                            email={email}
                            emailErrorMessage={errorMessage}
                            requestStateParams={requestStateParams}
                        />
                    )}
                </PageContainer>
            </div>
            <CaptchaVerificationPage
                show={showCaptcha}
                captchaWidget={captchaWidget}
                onCancel={cancelCaptcha}
                error={captchaError}
            />
        </>
    );
}

export default ForgotPasswordPage;
